import { useDropzone } from 'react-dropzone';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Upload,
  FileText,
  Loader2,
  Search,
  LayoutGrid,
  Rows,
  FileSearch,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Sparkles,
  Play,
} from 'lucide-react';
import { api } from '@/lib/api';
import { chunkedUpload, CHUNKED_UPLOAD_THRESHOLD } from '@/lib/chunkedUpload';
import { toast } from '@/stores/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { formatBytes, formatDate, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { extractApiError } from '@/lib/api';
import { Trash2, X as XIcon, Stethoscope, ShieldCheck, FileSearch2, BookOpen, FileSignature } from 'lucide-react';
import {
  ROLE_RANK,
  DOCUMENT_TYPE_CATEGORIES,
  DOCUMENT_TYPE_LABELS,
  type DocumentType,
} from '@praxara/shared/constants';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface DocRow {
  id: string;
  fileName: string;
  fileType: string;
  mimeType: string;
  fileSizeBytes: number;
  pageCount: number | null;
  status: string;
  documentType: string | null;
  category: string | null;
  metadata: { source?: string; confidence?: number; description?: string } | null;
  createdAt: string;
}

type CategoryKey = keyof typeof DOCUMENT_TYPE_CATEGORIES;

const CATEGORY_META: Record<CategoryKey, { label: string; tone: string }> = {
  CLINICAL: { label: 'Clinical', tone: 'bg-blue-500/10 text-blue-700 dark:text-blue-300' },
  REGULATORY: { label: 'Regulatory', tone: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
  CMC: { label: 'CMC', tone: 'bg-violet-500/10 text-violet-700 dark:text-violet-300' },
  PHARMACOVIGILANCE: { label: 'Pharmacovigilance', tone: 'bg-rose-500/10 text-rose-700 dark:text-rose-300' },
  QUALITY: { label: 'Quality', tone: 'bg-teal-500/10 text-teal-700 dark:text-teal-300' },
  OTHER: { label: 'Other', tone: 'bg-muted text-muted-foreground' },
};

function resolveCategory(documentType: string | null): CategoryKey | null {
  if (!documentType) return null;
  for (const key of Object.keys(DOCUMENT_TYPE_CATEGORIES) as CategoryKey[]) {
    if ((DOCUMENT_TYPE_CATEGORIES[key] as readonly string[]).includes(documentType)) return key;
  }
  return 'OTHER';
}

export function DocumentsPage() {
  const user = useAuthStore((s) => s.user);
  const canUpload = user && ROLE_RANK[user.role] >= ROLE_RANK['OPERATOR'];
  const qc = useQueryClient();

  const docs = useQuery({
    queryKey: ['documents'],
    queryFn: async () => (await api.get('/api/documents?pageSize=100')).data,
    refetchInterval: (q) => {
      const items = (q.state.data as { items?: { status: string }[] } | undefined)?.items;
      return items?.some((d) => d.status === 'UPLOADED' || d.status === 'PROCESSING') ? 3000 : false;
    },
  });

  const [bulkProgress, setBulkProgress] = useState<{ total: number; done: number; failed: number } | null>(null);
  const [chunkProgress, setChunkProgress] = useState<{ fileName: string; pct: number } | null>(null);

  async function uploadOne(file: File, meta?: { eCTDPath?: string; projectId?: string }) {
    const m = meta?.eCTDPath?.match(/(?:^|\/)M?(\d(?:\.\d+)*(?:\.[SP])?)/i);
    const eCTDModule = m ? m[1] : undefined;

    // Files larger than the threshold use the resumable chunked-upload path so
    // they survive disconnects and don't blow the JSON body limit.
    if (file.size > CHUNKED_UPLOAD_THRESHOLD) {
      setChunkProgress({ fileName: file.name, pct: 0 });
      try {
        await chunkedUpload(file, {
          projectId: meta?.projectId,
          eCTDModule,
          onProgress: ({ pct }) => setChunkProgress({ fileName: file.name, pct }),
        });
      } finally {
        setChunkProgress(null);
      }
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    if (meta?.eCTDPath) fd.append('eCTDPath', meta.eCTDPath);
    if (meta?.projectId) fd.append('projectId', meta.projectId);
    if (eCTDModule) fd.append('eCTDModule', eCTDModule);
    return api.post('/api/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  async function handleFiles(files: File[]) {
    if (files.length === 0) return;
    setBulkProgress({ total: files.length, done: 0, failed: 0 });
    let done = 0;
    let failed = 0;

    for (const f of files) {
      try {
        if (f.name.toLowerCase().endsWith('.zip')) {
          const JSZip = (await import('jszip')).default;
          const zip = await JSZip.loadAsync(f);
          const entries = Object.values(zip.files).filter((e) => !e.dir);
          for (const entry of entries) {
            const blob = await entry.async('blob');
            const mimeFromName =
              entry.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' :
              entry.name.toLowerCase().endsWith('.docx') ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
              entry.name.toLowerCase().endsWith('.txt') ? 'text/plain' : null;
            if (!mimeFromName) continue;
            const baseName = entry.name.split('/').pop() ?? entry.name;
            const file = new File([blob], baseName, { type: mimeFromName });
            try {
              await uploadOne(file, { eCTDPath: entry.name });
              done += 1;
            } catch {
              failed += 1;
            }
            setBulkProgress({ total: entries.length + files.length - 1, done, failed });
          }
        } else {
          await uploadOne(f);
          done += 1;
        }
      } catch {
        failed += 1;
      }
      setBulkProgress((p) => (p ? { ...p, done, failed } : p));
    }

    setBulkProgress(null);
    qc.invalidateQueries({ queryKey: ['documents'] });
    if (failed > 0) toast.warning('Upload complete with errors', `${done} succeeded · ${failed} failed`);
    else toast.success('Upload complete', `${done} document${done > 1 ? 's' : ''} queued for extraction`);
  }

  const upload = {
    isPending: bulkProgress !== null,
    mutate: (file: File) => handleFiles([file]),
    mutateMany: (files: File[]) => handleFiles(files),
  };

  const [filterCategory, setFilterCategory] = useState<'ALL' | CategoryKey>('ALL');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'PROCESSING' | 'READY' | 'FAILED'>('ALL');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const canBulkDelete = user && ROLE_RANK[user.role] >= ROLE_RANK['MANAGER'];

  function toggleSelect(id: string) {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkDelete() {
    if (!confirm(`Delete ${selectedIds.size} documents? This cannot be undone.`)) return;
    try {
      const res = await api.post('/api/documents/bulk/delete', { ids: Array.from(selectedIds) });
      toast.success('Deleted', `${res.data.deleted} documents removed${res.data.failed ? ` ? ${res.data.failed} failed` : ''}`);
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ['documents'] });
    } catch (err) {
      toast.error('Bulk delete failed', extractApiError(err).message);
    }
  }

  const items: DocRow[] = useMemo(() => (docs.data?.items as DocRow[]) ?? [], [docs.data]);

  const stats = useMemo(() => {
    const total = items.length;
    const processing = items.filter((d) => d.status === 'UPLOADED' || d.status === 'PROCESSING').length;
    const ready = items.filter((d) => d.status === 'TEXT_EXTRACTED' || d.status === 'PROCESSED').length;
    const failed = items.filter((d) => d.status === 'FAILED').length;
    const totalPages = items.reduce((acc, d) => acc + (d.pageCount ?? 0), 0);
    return { total, processing, ready, failed, totalPages };
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((d) => {
      if (filterCategory !== 'ALL') {
        const cat = resolveCategory(d.documentType);
        if (cat !== filterCategory) return false;
      }
      if (filterStatus !== 'ALL') {
        if (filterStatus === 'PROCESSING' && !(d.status === 'UPLOADED' || d.status === 'PROCESSING')) return false;
        if (filterStatus === 'READY' && !(d.status === 'TEXT_EXTRACTED' || d.status === 'PROCESSED')) return false;
        if (filterStatus === 'FAILED' && d.status !== 'FAILED') return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!d.fileName.toLowerCase().includes(q) && !(d.documentType ?? '').toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [items, filterCategory, filterStatus, search]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">Documents</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Upload regulatory documents (EPARs, RMPs, PSMFs, literature articles) and run AI-powered
            workflows against them — extract data, check compliance, or create ICSR cases.
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile icon={FileText} label="Documents" value={stats.total} hint={`${stats.totalPages.toLocaleString()} pages total`} />
        <StatTile icon={CheckCircle2} label="Ready for review" value={stats.ready} tone="success" />
        <StatTile icon={Clock} label="Processing" value={stats.processing} tone="info" />
        <StatTile icon={AlertTriangle} label="Failed" value={stats.failed} tone={stats.failed > 0 ? 'danger' : 'muted'} />
      </div>

      {/* Upload */}
      {canUpload && (
        <Dropzone
          onFiles={(files) => upload.mutateMany(files)}
          busy={upload.isPending}
          progress={bulkProgress}
          chunkProgress={chunkProgress}
        />
      )}

      {/* Guidance banner — explains what to do with documents */}
      {items.length > 0 && items.length <= 10 && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                <FileSearch2 className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-medium text-blue-900">What to do with your documents</div>
                <div className="mt-1 text-xs leading-relaxed text-blue-800">
                  Each document card shows contextual actions based on what type of document it is.
                  For example: <strong>EPARs</strong> can be analysed to extract product and safety data,
                  {' '}<strong>RMPs and PSMFs</strong> can be scanned for compliance gaps,
                  and <strong>literature articles</strong> can be used to create ICSR cases with full source traceability.
                  Click any action button on a document card to get started.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter bar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <CategoryPill current={filterCategory} setCurrent={setFilterCategory} label="All" value="ALL" count={items.length} />
          {(Object.keys(DOCUMENT_TYPE_CATEGORIES) as CategoryKey[]).map((cat) => {
            const count = items.filter(
              (d) => d.documentType && (DOCUMENT_TYPE_CATEGORIES[cat] as readonly string[]).includes(d.documentType),
            ).length;
            return (
              <CategoryPill
                key={cat}
                current={filterCategory}
                setCurrent={setFilterCategory}
                label={CATEGORY_META[cat].label}
                value={cat}
                count={count}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files..."
              className="h-9 w-56 pl-8"
            />
          </div>
          <StatusSelect value={filterStatus} onChange={setFilterStatus} />
          <div className="flex items-center rounded-md border p-0.5">
            <button
              onClick={() => setView('grid')}
              aria-label="Grid view"
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded transition-colors',
                view === 'grid' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView('list')}
              aria-label="List view"
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded transition-colors',
                view === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Rows className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-primary/40 bg-primary/5 px-4 py-2">
          <div className="text-sm">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
              <XIcon className="mr-1.5 h-3.5 w-3.5" /> Clear
            </Button>
            {canBulkDelete && (
              <Button size="sm" variant="destructive" onClick={bulkDelete}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete selected
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      {docs.isLoading ? (
        view === 'grid' ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-40" />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 border-b px-4 py-3 last:border-0">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20 ml-auto" />
              </div>
            ))}
          </div>
        )
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileSearch}
          title={items.length === 0 ? 'No documents yet' : 'No matches'}
          description={
            items.length === 0
              ? 'Upload your first regulatory or clinical document to begin the AI extraction workflow.'
              : 'Try adjusting your filters or search term.'
          }
        />
      ) : view === 'grid' ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((d) => (
            <DocCard key={d.id} doc={d} selected={selectedIds.has(d.id)} onToggle={() => toggleSelect(d.id)} />
          ))}
        </div>
      ) : (
        <DocList docs={filtered} selectedIds={selectedIds} onToggle={toggleSelect} />
      )}
    </div>
  );
}

// --- Stat tile ------------------------------------------

function StatTile({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'muted',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  hint?: string;
  tone?: 'muted' | 'success' | 'info' | 'danger';
}) {
  const toneCls: Record<string, string> = {
    muted: 'bg-muted/40 text-muted-foreground',
    success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    info: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    danger: 'bg-destructive/10 text-destructive',
  };
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-4">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-1 font-display text-2xl">{value.toLocaleString()}</div>
          {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
        </div>
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-md', toneCls[tone])}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}

// --- Category pill --------------------------------------

function CategoryPill<T extends string>({
  current,
  setCurrent,
  label,
  value,
  count,
}: {
  current: T;
  setCurrent: (v: T) => void;
  label: string;
  value: T;
  count: number;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => setCurrent(value)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {label}
      <span className={cn('inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1 text-[10px]', active ? 'bg-primary-foreground/20' : 'bg-muted')}>
        {count}
      </span>
    </button>
  );
}

function StatusSelect({
  value,
  onChange,
}: {
  value: 'ALL' | 'PROCESSING' | 'READY' | 'FAILED';
  onChange: (v: 'ALL' | 'PROCESSING' | 'READY' | 'FAILED') => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as typeof value)}
      className="h-9 rounded-md border border-input bg-background px-3 text-xs"
    >
      <option value="ALL">All statuses</option>
      <option value="PROCESSING">Processing</option>
      <option value="READY">Ready</option>
      <option value="FAILED">Failed</option>
    </select>
  );
}

// --- Doc card -------------------------------------------

interface DocAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  tone: string;
}

function getDocActions(doc: DocRow): DocAction[] {
  const dt = doc.documentType;
  const isReady = doc.status === 'TEXT_EXTRACTED' || doc.status === 'PROCESSED';
  if (!isReady) return [];

  const actions: DocAction[] = [];

  // EPAR / regulatory submissions → extract data
  if (['REGULATORY_SUBMISSION', 'MAA_APPLICATION', 'NDA_BLA_APPLICATION', 'ECTD_MODULE', 'SMPC'].includes(dt ?? '')) {
    actions.push({ label: 'Extract product data', icon: FileSearch2, to: `/documents/${doc.id}?action=epar-extraction`, tone: 'bg-indigo-500/10 text-indigo-700 hover:bg-indigo-500/20' });
  }

  // CSR / clinical study reports → extract study data
  if (['CLINICAL_STUDY_REPORT', 'CLINICAL_OVERVIEW', 'CLINICAL_SUMMARY'].includes(dt ?? '')) {
    actions.push({ label: 'Extract study data', icon: FileSearch2, to: `/documents/${doc.id}?action=csr-extraction`, tone: 'bg-blue-500/10 text-blue-700 hover:bg-blue-500/20' });
  }

  // RMP → compliance check or extract risk data
  if (dt === 'RISK_MANAGEMENT_PLAN') {
    actions.push({ label: 'Compliance check', icon: ShieldCheck, to: `/documents/${doc.id}?action=compliance-scan`, tone: 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20' });
  }

  // PSMF → compliance scan
  if (dt === 'PSMF') {
    actions.push({ label: 'Run compliance scan', icon: ShieldCheck, to: `/documents/${doc.id}?action=compliance-scan`, tone: 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20' });
  }

  // Literature article → create ICSR or screen for signals
  if (dt === 'LITERATURE_ARTICLE') {
    actions.push({ label: 'Create ICSR from this', icon: Stethoscope, to: `/pv?newCase=1&sourceDocId=${doc.id}&sourceType=literature&sourceChannel=literature`, tone: 'bg-rose-500/10 text-rose-700 hover:bg-rose-500/20' });
    actions.push({ label: 'Screen for signals', icon: BookOpen, to: `/documents/${doc.id}?action=signal-screen`, tone: 'bg-amber-500/10 text-amber-700 hover:bg-amber-500/20' });
  }

  // PSUR/PBRER → draft next version
  if (['PSUR', 'PBRER'].includes(dt ?? '')) {
    actions.push({ label: 'Draft PSUR section', icon: FileSignature, to: `/documents/${doc.id}?action=psur-draft`, tone: 'bg-violet-500/10 text-violet-700 hover:bg-violet-500/20' });
  }

  // SOPs / quality docs → compliance check
  if (['SOP', 'WORK_INSTRUCTION', 'QUALITY_MANUAL', 'VALIDATION_MASTER_PLAN'].includes(dt ?? '')) {
    actions.push({ label: 'Check compliance', icon: ShieldCheck, to: `/documents/${doc.id}?action=compliance-scan`, tone: 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20' });
  }

  // Fallback: any extracted document can run a generic workflow
  if (actions.length === 0) {
    actions.push({ label: 'Run workflow', icon: Play, to: `/documents/${doc.id}`, tone: 'bg-primary/10 text-primary hover:bg-primary/20' });
  }

  return actions;
}

function DocCard({ doc, selected, onToggle }: { doc: DocRow; selected: boolean; onToggle: () => void }) {
  const navigate = useNavigate();
  const cat = resolveCategory(doc.documentType);
  const typeLabel = doc.documentType ? DOCUMENT_TYPE_LABELS[doc.documentType as DocumentType] : 'Unclassified';
  const ocrSource = doc.metadata?.source;
  const actions = getDocActions(doc);
  return (
    <div
      className={cn(
        'group relative block rounded-lg border bg-card p-4 transition-all hover:border-primary/40 hover:shadow-md',
        selected && 'border-primary ring-2 ring-primary/30',
      )}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'absolute left-2 top-2 h-4 w-4 accent-primary transition-opacity',
          !selected && 'opacity-0 group-hover:opacity-100',
        )}
        aria-label="Select document"
      />
      <Link to={`/documents/${doc.id}`} className="block">
      <div className="flex items-start gap-3">
        <FileIcon mimeType={doc.mimeType} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium leading-tight group-hover:text-primary">{doc.fileName}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {cat && (
              <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium', CATEGORY_META[cat].tone)}>
                {CATEGORY_META[cat].label}
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">{typeLabel}</span>
          </div>
        </div>
        <DocStatusBadge status={doc.status} />
      </div>

      {doc.metadata?.description && (
        <div className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{doc.metadata.description}</div>
      )}
      </Link>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-3">
        {actions.map((a) => (
          <button
            key={a.label}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(a.to); }}
            className={cn('inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors', a.tone)}
          >
            <a.icon className="h-3 w-3" /> {a.label}
          </button>
        ))}
        <div className="flex flex-1 items-center justify-end gap-3 text-xs text-muted-foreground">
          <span>{doc.pageCount ?? '—'} pp</span>
          <span>{formatBytes(doc.fileSizeBytes)}</span>
          <span>{formatRelative(doc.createdAt)}</span>
        </div>
      </div>

      {ocrSource && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Sparkles className="h-3 w-3" />
          OCR: {ocrLabel(ocrSource)}
          {typeof doc.metadata?.confidence === 'number' && (
            <span>· {Math.round((doc.metadata.confidence ?? 0) * 100)}% conf.</span>
          )}
        </div>
      )}
    </div>
  );
}

function FileIcon({ mimeType }: { mimeType: string }) {
  const isPdf = mimeType === 'application/pdf';
  const isDocx = mimeType.includes('wordprocessingml');
  const label = isPdf ? 'PDF' : isDocx ? 'DOCX' : 'TXT';
  const tone = isPdf
    ? 'bg-rose-500/10 text-rose-600'
    : isDocx
    ? 'bg-blue-500/10 text-blue-600'
    : 'bg-muted text-muted-foreground';
  return (
    <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-display text-[10px] font-semibold', tone)}>
      {label}
    </div>
  );
}

function ocrLabel(source: string): string {
  switch (source) {
    case 'azure-di':
      return 'Azure Document Intelligence';
    case 'pdf-parse':
      return 'pdf-parse fallback';
    case 'mammoth':
      return 'mammoth';
    case 'txt':
      return 'plain text';
    default:
      return source;
  }
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

// --- Doc list -------------------------------------------

function DocList({ docs, selectedIds, onToggle }: { docs: DocRow[]; selectedIds: Set<string>; onToggle: (id: string) => void }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {docs.map((d) => {
            const cat = resolveCategory(d.documentType);
            const selected = selectedIds.has(d.id);
            return (
              <div
                key={d.id}
                className={cn(
                  'group flex items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/40',
                  selected && 'bg-primary/5',
                )}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggle(d.id)}
                  className="h-4 w-4 accent-primary"
                  aria-label="Select document"
                />
                <Link to={`/documents/${d.id}`} className="flex flex-1 items-center gap-4">
                <FileIcon mimeType={d.mimeType} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium group-hover:text-primary">{d.fileName}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {cat && (
                      <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium', CATEGORY_META[cat].tone)}>
                        {CATEGORY_META[cat].label}
                      </span>
                    )}
                    {d.documentType && <span>{DOCUMENT_TYPE_LABELS[d.documentType as DocumentType]}</span>}
                  </div>
                </div>
                <div className="hidden w-20 text-right text-xs text-muted-foreground md:block">{d.pageCount ?? '—'} pp</div>
                <div className="hidden w-24 text-right text-xs text-muted-foreground md:block">{formatBytes(d.fileSizeBytes)}</div>
                <div className="w-28 text-right text-xs text-muted-foreground">{formatRelative(d.createdAt)}</div>
                <DocStatusBadge status={d.status} />
                </Link>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// --- Dropzone -------------------------------------------

function Dropzone({
  onFiles,
  busy,
  progress,
  chunkProgress,
}: {
  onFiles: (files: File[]) => void;
  busy: boolean;
  progress: { total: number; done: number; failed: number } | null;
  chunkProgress: { fileName: string; pct: number } | null;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
    // No maxFiles — accept as many as they drop
    maxSize: 2 * 1024 * 1024 * 1024, // 2 GB per file — chunked upload handles >50 MB
    onDrop: (files) => {
      if (files.length > 0) onFiles(files);
    },
    disabled: busy,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'group cursor-pointer rounded-lg border-2 border-dashed bg-card/40 p-6 text-center transition-all',
        isDragActive
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/20 hover:border-primary/40 hover:bg-accent/20',
      )}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center gap-3">
        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-full transition-colors',
            isDragActive ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary group-hover:bg-primary/20',
          )}
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
        </div>
        <div>
          <p className="text-sm font-medium">
            {busy && progress
              ? `Uploading ${progress.done}/${progress.total}${progress.failed > 0 ? ` ? ${progress.failed} failed` : ''}`
              : isDragActive
                ? 'Drop to upload'
                : 'Drag documents or a ZIP here, or click to browse'}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            PDF, DOCX, TXT, or ZIP · drag multiple files or a folder
          </p>
        </div>
        {busy && progress && (
          <div className="w-full max-w-md">
            <div className="h-1.5 rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${progress.total > 0 ? ((progress.done + progress.failed) / progress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}
        {chunkProgress && (
          <div className="w-full max-w-md">
            <div className="mb-1 truncate text-xs text-muted-foreground">
              Large file: {chunkProgress.fileName} — {chunkProgress.pct}%
            </div>
            <div className="h-1.5 rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${chunkProgress.pct}%` }}
              />
            </div>
          </div>
        )}
        {!busy && (
          <Button size="sm" variant="secondary" type="button" className="pointer-events-none">
            Choose files
          </Button>
        )}
      </div>
    </div>
  );
}

// --- Status badge ---------------------------------------

function DocStatusBadge({ status }: { status: string }) {
  const variant: Record<string, 'secondary' | 'info' | 'success' | 'destructive' | 'warning'> = {
    UPLOADED: 'secondary',
    PROCESSING: 'info',
    TEXT_EXTRACTED: 'success',
    PROCESSED: 'success',
    FAILED: 'destructive',
  };
  const label: Record<string, string> = {
    UPLOADED: 'Queued',
    PROCESSING: 'Processing',
    TEXT_EXTRACTED: 'Extracted',
    PROCESSED: 'Processed',
    FAILED: 'Failed',
  };
  return (
    <Badge variant={variant[status] ?? 'secondary'} className="whitespace-nowrap">
      {label[status] ?? status}
    </Badge>
  );
}
